import { createError } from "@crm/errors/factories";
import { Effect, Result, Schema, type SchemaError, SchemaIssue } from "effect";
import type { ParseOptions } from "effect/SchemaAST";

export type EffectSchemaParseError = SchemaError.SchemaError;

export type StandardSchemaIssue = {
  readonly message: string;
  readonly path?: readonly PropertyKey[];
};

export type StandardSchemaV1<Input = unknown, Output = Input> = {
  readonly "~standard": {
    readonly version: 1;
    readonly vendor: string;
    readonly validate: (
      value: unknown,
    ) =>
      | { readonly value: Output; readonly issues?: undefined }
      | { readonly issues: readonly StandardSchemaIssue[] };
    readonly types?: {
      readonly input: Input;
      readonly output: Output;
    };
  };
};

export type ParseableEffectSchema<A, I = unknown> = Schema.ConstraintCodec<A, I> &
  StandardSchemaV1<I, A> & {
    readonly parse: (input: unknown) => A;
    readonly safeParse: (input: unknown) =>
      | { readonly success: true; readonly data: A }
      | {
          readonly success: false;
          readonly error: Error;
        };
  };

export type BoundaryIssue = {
  readonly code: string;
  readonly path: readonly PropertyKey[];
  readonly message: string;
};

export type BoundaryParseError = {
  readonly issues: readonly BoundaryIssue[];
  readonly flatten: () => {
    readonly fieldErrors: Record<string, string[]>;
  };
};

export type BoundaryParseResult<A> =
  | { readonly success: true; readonly data: A }
  | { readonly success: false; readonly error: BoundaryParseError };

export function optionalWithDecodedDefault<S extends Schema.Constraint>(
  schema: S,
  fallback: () => NoInfer<S["Encoded"]>,
) {
  return Schema.withDecodingDefault<S>(Effect.sync(fallback))(schema);
}

export function formatEffectSchemaError(error: EffectSchemaParseError): string {
  return error.message;
}

export function formatEffectSchemaIssues(error: EffectSchemaParseError): readonly BoundaryIssue[] {
  return SchemaIssue.makeFormatterStandardSchemaV1()(error.issue).issues.map((issue) => ({
    code: "SchemaIssue",
    path: (issue.path ?? []).map((segment) =>
      typeof segment === "object" && segment !== null && "key" in segment ? segment.key : segment,
    ),
    message: issue.message,
  }));
}

export function safeParseBoundary<A, I>(
  schema: Schema.ConstraintCodec<A, I>,
  input: unknown,
  options?: ParseOptions,
): BoundaryParseResult<A> {
  const decoded = Schema.decodeUnknownResult(schema, options)(input);
  if (Result.isSuccess(decoded)) {
    return {
      success: true,
      data: decoded.success,
    };
  }

  const issues = formatEffectSchemaIssues(decoded.failure);
  return {
    success: false,
    error: {
      issues,
      flatten: () => ({
        fieldErrors: issues.reduce<Record<string, string[]>>((acc, issue) => {
          const key = String(issue.path[0] ?? "_root");
          acc[key] = [...(acc[key] ?? []), issue.message];
          return acc;
        }, {}),
      }),
    },
  };
}

export function decodeBoundaryEffect<A, I, R, E>(
  schema: Schema.ConstraintCodec<A, I, R>,
  input: unknown,
  mapError: (error: EffectSchemaParseError, message: string) => E,
  options?: ParseOptions,
): Effect.Effect<A, E, R> {
  return Schema.decodeUnknownEffect(
    schema,
    options,
  )(input).pipe(Effect.mapError((error) => mapError(error, formatEffectSchemaError(error))));
}

export function decodeBoundarySync<A, I>(
  schema: Schema.ConstraintCodec<A, I>,
  input: unknown,
  mapError: (error: EffectSchemaParseError, message: string) => unknown,
  options?: ParseOptions,
): A {
  const decoded = Schema.decodeUnknownResult(schema, options)(input);
  return Result.getOrThrowWith(decoded, (error) => mapError(error, formatEffectSchemaError(error)));
}

export function withParse<A, I>(
  schema: Schema.ConstraintCodec<A, I>,
  options?: ParseOptions,
  preprocess: (input: unknown) => unknown = (input) => input,
): ParseableEffectSchema<A, I> {
  const decode = (input: unknown) => Schema.decodeUnknownResult(schema, options)(preprocess(input));

  return Object.assign(schema, {
    parse(input: unknown): A {
      const decoded = decode(input);
      return Result.getOrThrowWith(decoded, (error) =>
        createError.validation(formatEffectSchemaError(error)),
      );
    },
    safeParse(input: unknown) {
      const decoded = decode(input);
      if (Result.isSuccess(decoded)) {
        return { success: true as const, data: decoded.success };
      }

      return {
        success: false as const,
        error: createError.validation(formatEffectSchemaError(decoded.failure)),
      };
    },
    "~standard": {
      version: 1 as const,
      vendor: "effect",
      validate(value: unknown) {
        const decoded = decode(value);
        if (Result.isSuccess(decoded)) {
          return { value: decoded.success };
        }

        return {
          issues: formatEffectSchemaIssues(decoded.failure).map((issue) => ({
            message: issue.message,
            path: issue.path,
          })),
        };
      },
      types: undefined as unknown as { readonly input: I; readonly output: A },
    },
  });
}

export {
  coerceDate,
  dateString,
  emailString,
  emptyOr,
  monthString,
  nonNegativeInteger,
  normalizeArrayNulls,
  normalizeNulls,
  normalizeUndefineds,
  positiveInteger,
  requiredString,
  requiredStringMax,
  stringArrayOrNull,
  stringMax,
  urlString,
  uuidString,
} from "./fields";

export const unknownRecordSchema = <A>(): ParseableEffectSchema<A> =>
  withParse(Schema.instanceOf(Object)) as unknown as ParseableEffectSchema<A>;

/** Canonical payload schema for RPCs that intentionally take no input. */
export const NoInput = withParse(Schema.Undefined);

/** @deprecated Use NoInput. Kept temporarily for domain migration only. */
export const undefinedInputSchema = NoInput;
